import { create } from 'zustand';
// import { persist } from 'zustand/middleware'; // DISABLED for development - always fetch fresh metadata
import { getGameMetadata, validateMetadata } from '@api/metadataApi';
import feedback from '@components/ui/general/feedback';

/**
 * useMetadataStore - Manages game metadata and rules
 * 
 * This store is responsible for:
 * - Fetching game metadata from backend once
 * - Providing stat constraints and game rules
 * - Helper methods for applying constraints
 * 
 * No subscriptions needed - other stores call getState() to access metadata
 */
const useMetadataStore = create(
  // persist( // DISABLED for development - always fetch fresh metadata
    (set, get) => ({
      // --- STATE ---
      metadata: null,
      loading: false,
      loaded: false,
      error: null,
      version: 1.1, // Increment this to force cache refresh

      // --- ACTIONS ---
  
  /**
   * Fetch metadata from backend
   * Should be called once when app initializes
   */
  fetchMetadata: async () => {
    const state = get();
    
    // Don't fetch if already loaded or loading
    if (state.loaded || state.loading) {
      // console.info('[MetadataStore] Metadata already loaded or loading, skipping fetch');
      return state.metadata;
    }

    set({ loading: true, error: null });

    try {
      const metadata = await getGameMetadata();
      
      // Validate the metadata structure
      if (!validateMetadata(metadata)) {
        throw new Error('Invalid metadata structure received');
      }

      set({ 
        metadata, 
        loaded: true, 
        loading: false,
        error: null 
      });

      //console.info('[MetadataStore] Metadata loaded successfully');
      return metadata;
      
    } catch (error) {
      console.error('[MetadataStore] Failed to load metadata:', error);
      
      set({ 
        loading: false, 
        error: error.message || 'Failed to load game metadata',
        loaded: false 
      });
      
      // This is critical - game cannot function without metadata
      feedback.error('Failed to load game rules. Please refresh the page.');
      throw error;
    }
  },

  /**
   * Reset the store (useful for testing)
   */
  reset: () => {
    set({
      metadata: null,
      loading: false,
      loaded: false,
      error: null
    });
  },

  // --- HELPER METHODS ---
  
  /**
   * Get constraint for a specific stat
   * @param {string} stat - The stat name
   * @returns {object} Constraint object with min/max values
   */
  getStatConstraint: (stat) => {
    const { metadata } = get();
    
    if (!metadata?.stat_constraints?.[stat]) {
      console.warn(`[MetadataStore] No constraint found for stat: ${stat}`);
      return { min: null, max: null };
    }
    
    return metadata.stat_constraints[stat];
  },

  /**
   * Apply constraint to a stat value
   * @param {string} stat - The stat name
   * @param {number} value - The value to constrain
   * @returns {number} The constrained value
   */
  applyStatConstraint: (stat, value) => {
    const constraint = get().getStatConstraint(stat);
    
    if (!constraint) return value;
    
    let result = value;
    
    // Apply minimum constraint
    if (constraint.min !== null && constraint.min !== undefined) {
      result = Math.max(constraint.min, result);
    }
    
    // Apply maximum constraint  
    if (constraint.max !== null && constraint.max !== undefined) {
      result = Math.min(constraint.max, result);
    }
    
    return result;
  },

  /**
   * Check if metadata is ready
   * @returns {boolean} True if metadata is loaded
   */
  isReady: () => {
    return get().loaded && get().metadata !== null;
  },

  /**
   * Get all tracked stats
   * @returns {array} List of stat names that should be tracked
   */
  getTrackedStats: () => {
    const { metadata } = get();
    
    if (!metadata?.tracked_stats) {
      // Fallback to default list
      return [
        'armor', 'strength', 'agility', 'psi', 'will',
        'vision', 'max_movement', 'ap', 'movement', 'hp'
      ];
    }
    
    return metadata.tracked_stats;
  },

  /**
   * Get game rules
   * @returns {object} Game rules object
   */
  getGameRules: () => {
    const { metadata } = get();
    return metadata?.game_rules || {};
  },

  /**
   * Get combat rules
   * @returns {object} Combat rules object
   */
  getCombatRules: () => {
    const { metadata } = get();
    return metadata?.combat_rules || {};
  },

  /**
   * Get effect rules
   * @returns {object} Effect rules object
   */
  getEffectRules: () => {
    const { metadata } = get();
    return metadata?.effect_rules || {};
  },

  /**
   * Get effect by ID
   * @param {number} effectId - The effect ID
   * @returns {object|null} Effect object or null if not found
   */
  getEffectById: (effectId) => {
    const { metadata } = get();

    if (!metadata?.effects) {
      console.warn('[MetadataStore] No effects loaded in metadata',metadata);
      return null;
    }

    const effect = metadata.effects.find(e => e.id === effectId);

    if (!effect) {
      //console.warn('[MetadataStore] Effect not found for ID:', effectId);
      return null;
    }

    return effect;
  },

  /**
   * Get effect by slug
   * @param {string} effectSlug - The effect slug (e.g., 'sword_counter_attack')
   * @returns {object|null} Effect object or null if not found
   */
  getEffectBySlug: (effectSlug) => {
    const { metadata } = get();

    if (!metadata?.effects) {
      console.warn('[MetadataStore] No effects loaded in metadata');
      return null;
    }

    const effect = metadata.effects.find(e => e.slug === effectSlug);

    if (!effect) {
      return null;
    }

    return effect;
  },

  /**
   * Get all effects
   * @returns {array} List of all effects
   */
  getAllEffects: () => {
    const { metadata } = get();
    return metadata?.effects || [];
  },

  /**
   * Hydrate a unit effect with metadata template data
   * Adds missing category and properties from the effect template
   * @param {object} unitEffect - The unit effect to hydrate
   * @returns {object} The hydrated effect (mutates original)
   */
  hydrateUnitEffect: (unitEffect) => {
    // Must have effect_id to hydrate
    if (!unitEffect?.effect_id) {
      console.error('[MetadataStore] Cannot hydrate effect without effect_id:', unitEffect);
      return unitEffect;
    }

    const effectTemplate = get().getEffectById(unitEffect.effect_id);
    
    if (!effectTemplate) {
      console.error('[MetadataStore] Effect template not found for ID:', unitEffect.effect_id);
      console.trace();
      return unitEffect;
    }

    // Hydrate missing fields from template
    if (!unitEffect.effect_category && effectTemplate.category) {
      unitEffect.effect_category = effectTemplate.category;
    }

    // Only hydrate properties if completely missing
    // Unit-specific properties should override template defaults
    if (!unitEffect.properties && effectTemplate.properties) {
      unitEffect.properties = effectTemplate.properties;
    }

    // Also hydrate name and slug if missing
    if (!unitEffect.effect_name && effectTemplate.name) {
      unitEffect.effect_name = effectTemplate.name;
    }

    if (!unitEffect.effect_slug && effectTemplate.slug) {
      unitEffect.effect_slug = effectTemplate.slug;
    }

    return unitEffect;
  },

  /**
   * Hydrate all effects for a unit
   * @param {object} unit - The unit with active_effects array
   * @returns {object} The unit with hydrated effects (mutates original)
   */
  hydrateUnitEffects: (unit) => {
    if (!unit?.active_effects || !Array.isArray(unit.active_effects)) {
      return unit;
    }

    unit.active_effects.forEach(effect => {
      get().hydrateUnitEffect(effect);
    });

    return unit;
  }
    })
    // {
    //   name: 'xhelos-metadata', // localStorage key
    //   partialize: (state) => ({
    //     metadata: state.metadata,
    //     loaded: state.loaded,
    //     version: state.version
    //   }), // Only persist metadata and loaded state
    //   version: 1, // Storage version
    //   migrate: (persistedState, version) => {
    //     // Handle migration if storage version changes
    //     if (version !== 1) {
    //       return { ...persistedState, loaded: false }; // Force re-fetch
    //     }
    //     return persistedState;
    //   }
    // }
  // )
);

export default useMetadataStore;